import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";

interface MobileStoryTinyListCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  showDivider?: boolean;
}

/**
 * Ultra-compact list card — Bloomberg / CNBC density.
 * Tiny square thumbnail (48px) + headline + time.
 * Maximum stories per screen for power-user scanning.
 */
export function MobileStoryTinyListCard({
  story,
  exploreBase,
  showDivider = true,
}: MobileStoryTinyListCardProps) {
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <div>
      <Link
        className="flex items-center gap-3 no-underline active:opacity-80"
        href={`${exploreBase}/stories/${story.storyId}`}
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        {/* Tiny square thumbnail */}
        <div className="relative h-[48px] w-[48px] shrink-0 overflow-hidden rounded-md bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
          <Image
            alt=""
            className="h-full w-full object-cover"
            fill
            sizes="48px"
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
          {story.isNew && (
            <div className="absolute top-0.5 left-0.5 h-2 w-2 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)] shadow-[0_0_0_2px_var(--explore-new-indicator-ring)]" />
          )}
        </div>

        {/* Headline + time */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h3
            className="line-clamp-2 text-pretty font-semibold text-[0.88rem] text-[var(--explore-text-primary)] leading-[1.3]"
            style={{ fontFamily: "var(--explore-card-title-stack)" }}
          >
            {story.headline}
          </h3>
          <span className="text-[0.68rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </Link>

      {showDivider && (
        <div className="mt-2.5 border-[var(--explore-border-light)] border-b" />
      )}
    </div>
  );
}
